import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setActivePage } from "../store/activePage";
import DataTable from "react-data-table-component";
import getTokens from "../hooks/getTokens";
import formatCustomDate from "../helpers/customDateFormat";
import { SuccessToast, ErrorToast } from "../components/toastify";

const isCurrentOrFutureEvent = (item) => {
  const eventDate = new Date(item?.event_date || "");
  if (Number.isNaN(eventDate.getTime())) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  eventDate.setHours(0, 0, 0, 0);

  return eventDate >= today;
};

function TokenList() {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [tokenList, setTokenList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dispatch(
      setActivePage({
        headerName: "Token List",
        activeMenu: "tokenList",
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    fetchTokens();
  }, []);

  const fetchTokens = async () => {
    setLoading(true);
    try {
      const tokenDetails = await getTokens(dispatch);
      const upcomingTokens = Array.isArray(tokenDetails)
        ? tokenDetails.filter(isCurrentOrFutureEvent)
        : [];
      if (upcomingTokens.length > 0) {
        setTokenList(upcomingTokens);
        setFilteredData(upcomingTokens);
      } else {
        setTokenList([]);
        setFilteredData([]);
      }
    } catch (error) {
      console.error("Error fetching tokens:", error);
      ErrorToast("Failed to fetch tokens");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (token) => {
    navigator.clipboard
      .writeText(token)
      .then(() => {
        SuccessToast(`Token ${token} copied to clipboard`);
      })
      .catch((err) => {
        console.error("Copy failed:", err);
        ErrorToast("Failed to copy token");
      });
  };

  const columns = [
    {
      name: "Created On",
      selector: (row) => formatCustomDate(row.created_on),
      sortable: true,
      minWidth: "110px",
      wrap: true,
    },
    {
      name: "Name",
      selector: (row) => `${row.name || ""} ${row.family_name || ""}`.trim(),
      sortable: true,
      minWidth: "150px",
      wrap: true,
    },
    {
      name: "Mobile",
      selector: (row) => row.mobile || "-",
      minWidth: "110px",
    },
    {
      name: "Token",
      selector: (row) => row.token,
      sortable: true,
      minWidth: "120px",
      cell: (row) => (
        <span
          onClick={() => handleCopy(row.token)}
          className="px-2.5 py-1 bg-slate-50 border border-slate-200/70 font-mono text-xs rounded-lg text-slate-700 cursor-pointer hover:border-amber-500 hover:text-amber-700 transition"
          title="Click to copy token"
        >
          {row.token}
        </span>
      ),
    },
    {
      name: "Event Type",
      selector: (row) => row.event_type,
      sortable: true,
      minWidth: "120px",
      wrap: true,
    },
    {
      name: "Event Date",
      selector: (row) => formatCustomDate(row.event_date),
      sortable: true,
      minWidth: "110px",
    },
    {
      name: "Eventors",
      selector: (row) =>
        [row.eventor_1, row.eventor_2].filter(Boolean).join(", "),
      minWidth: "160px",
      wrap: true,
      cell: (row) =>
        [row.eventor_1, row.eventor_2].filter(Boolean).join(", ") || "—",
    },
    {
      name: "Event Address",
      selector: (row) => row.event_address,
      sortable: true,
      minWidth: "200px",
      wrap: true,
      cell: (row) => (
        <div title={row.event_address} className="truncate max-w-xs text-xs text-slate-500">
          {row.event_address}
        </div>
      ),
    },
    {
      name: "Eventor Name",
      selector: (row) =>
        `${row.first_name || ""} ${row.last_name || ""}`.trim(),
      sortable: true,
      minWidth: "150px",
      wrap: true,
    },
    {
      name: "Eventor Mobile",
      selector: (row) => row.eventor_mobile || row.customer_mobile || "-",
      minWidth: "110px",
    },
    {
      name: "Status",
      selector: (row) => (row.active ? "Active" : "In-Active"),
      sortable: true,
      minWidth: "90px",
      cell: (row) => (
        <span
          className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
            row.active
              ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
              : "bg-rose-50 text-rose-700 border border-rose-100"
          }`}
        >
          {row.active ? "Active" : "Inactive"}
        </span>
      ),
    },
  ];

  const customStyles = {
    table: {
      style: {
        backgroundColor: "#ffffff",
      },
    },
    headRow: {
      style: {
        borderTop: "1px solid #f1f5f9",
        borderBottom: "1px solid #e2e8f0",
        backgroundColor: "#f8fafc",
        minHeight: "48px",
      },
    },
    headCells: {
      style: {
        color: "#475569",
        fontWeight: "750",
        fontSize: "11px",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        paddingLeft: "12px",
        paddingRight: "12px",
      },
    },
    rows: {
      style: {
        minHeight: "48px",
        fontSize: "12.5px",
        color: "#334155",
        "&:nth-of-type(even)": {
          backgroundColor: "#f8fafc",
        },
        "&:hover": {
          backgroundColor: "rgba(217,119,6,0.04) !important",
          transition: "background-color 0.15s ease",
        },
      },
    },
    cells: {
      style: {
        paddingLeft: "12px",
        paddingRight: "12px",
        borderBottom: "1px solid #f1f5f9",
      },
    },
    pagination: {
      style: {
        backgroundColor: "#ffffff",
        borderTop: "1px solid #e2e8f0",
        color: "#475569",
        fontSize: "11px",
        padding: "8px 16px",
      },
      pageButtonsStyle: {
        color: "#475569",
        fill: "#475569",
        borderRadius: "8px",
        transition: "all 0.2s ease",
        "&:hover:not(:disabled)": {
          backgroundColor: "#f1f5f9",
          color: "#d97706",
          fill: "#d97706",
        },
        "&:focus": {
          outline: "none",
          backgroundColor: "#f1f5f9",
        },
      },
    },
  };

  const handleSearch = (event) => {
    const searchText = event.target.value.toLowerCase();
    setSearch(searchText);

    if (!searchText.trim()) {
      setFilteredData(tokenList);
      return;
    }

    const filtered = tokenList.filter((item) => {
      const fullName =
        `${item.name || ""} ${item.family_name || ""}`.toLowerCase();
      return (
        fullName.includes(searchText) ||
        (item.mobile && item.mobile.toString().includes(searchText)) ||
        (item.token && item.token.toString().includes(searchText)) ||
        (item.event_type &&
          item.event_type.toLowerCase().includes(searchText)) ||
        (item.event_address &&
          item.event_address.toLowerCase().includes(searchText)) ||
        (item.eventor_1 &&
          item.eventor_1.toLowerCase().includes(searchText)) ||
        (item.eventor_2 &&
          item.eventor_2.toLowerCase().includes(searchText)) ||
        (item.first_name &&
          item.first_name.toLowerCase().includes(searchText)) ||
        (item.last_name && item.last_name.toLowerCase().includes(searchText))
      );
    });

    setFilteredData(filtered);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] overflow-hidden">
        <div className="p-6 border-b border-slate-100 bg-slate-50/50">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-xl font-bold text-slate-800">Token History</h1>
              <p className="text-xs text-slate-500 mt-1">
                Manage and view all generated contribution tokens
              </p>
            </div>
            <button
              onClick={fetchTokens}
              className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 text-xs font-bold rounded-xl transition"
            >
              <span>↻</span>
              <span>Refresh</span>
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="relative w-full sm:w-80">
              <input
                type="text"
                placeholder="🔍 Search by name, mobile, token..."
                value={search}
                onChange={handleSearch}
                className="block w-full pl-10 pr-4 py-2.5 text-xs border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-600 transition"
              />
            </div>
            <div className="text-xs text-slate-500">
              Showing {filteredData.length} of {tokenList.length} tokens
            </div>
          </div>

          <DataTable
            columns={columns}
            data={filteredData}
            pagination
            highlightOnHover
            customStyles={customStyles}
            progressPending={loading}
            noDataComponent={
              <div className="text-center py-12 text-slate-400 text-xs">
                {loading ? "Loading tokens..." : "No tokens found"}
              </div>
            }
            responsive
            overflowY={false}
            fixedHeader
            fixedHeaderScrollHeight="600px"
          />
        </div>
      </div>
    </div>
  );
}

export default TokenList;
